import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useIsomorphicLayoutEffect, prefersReducedMotion } from '@/lib/gsap'

/* cubic-bezier(.19, 1, .22, 1) solved by hand. GSAP accepts a plain progress
   function as an ease, so the reference curve can be reproduced exactly
   without pulling in CustomEase. */
const bezier = (p1x: number, p1y: number, p2x: number, p2y: number): ((x: number) => number) => {
    const cx = 3 * p1x
    const bx = 3 * (p2x - p1x) - cx
    const ax = 1 - cx - bx
    const cy = 3 * p1y
    const by = 3 * (p2y - p1y) - cy
    const ay = 1 - cy - by

    const sampleX = (t: number): number => ((ax * t + bx) * t + cx) * t
    const sampleY = (t: number): number => ((ay * t + by) * t + cy) * t
    const slopeX = (t: number): number => (3 * ax * t + 2 * bx) * t + cx

    return (x: number): number => {
        let t = x

        for (let i = 0; i < 8; i += 1) {
            const error = sampleX(t) - x

            if (Math.abs(error) < 0.0001) {
                return sampleY(t)
            }

            const slope = slopeX(t)

            if (Math.abs(slope) < 0.000001) {
                break
            }

            t -= error / slope
        }

        let low = 0
        let high = 1
        t = x

        for (let i = 0; i < 24; i += 1) {
            const value = sampleX(t)

            if (Math.abs(value - x) < 0.0001) {
                break
            }

            if (value < x) {
                low = t
            } else {
                high = t
            }

            t = (high + low) / 2
        }

        return sampleY(t)
    }
}

const ODOMETER_EASE = bezier(0.19, 1, 0.22, 1)
const DURATION = 0.45
const TRAVEL = 110

interface OdometerProps {
    value: string
    /* 1 rolls the new value up from below, -1 drops it in from above. */
    direction?: number
    className?: string
}

/* Direction-aware digit swap. A clipped shell holds an invisible copy of the
   value to reserve the box, then the incoming and outgoing copies slide
   through it in opposite directions. */
export const Odometer: FC<OdometerProps> = ({
    value,
    direction = 1,
    className
}): ReactNode => {
    const incomingRef = useRef<HTMLSpanElement>(null)
    const outgoingRef = useRef<HTMLSpanElement>(null)
    const previous = useRef(value)

    useIsomorphicLayoutEffect(() => {
        const incoming = incomingRef.current
        const outgoing = outgoingRef.current

        if (!incoming || !outgoing) {
            return
        }

        const from = previous.current
        previous.current = value

        if (from === value || prefersReducedMotion()) {
            gsap.set(incoming, { yPercent: 0, opacity: 1 })
            gsap.set(outgoing, { opacity: 0 })

            return
        }

        outgoing.textContent = from

        const sign = direction >= 0 ? 1 : -1

        gsap.set(outgoing, { yPercent: 0, opacity: 1 })
        gsap.set(incoming, { yPercent: sign * TRAVEL, opacity: 1 })

        const tweens = [
            gsap.to(incoming, {
                yPercent: 0,
                duration: DURATION,
                ease: ODOMETER_EASE,
                overwrite: true
            }),
            gsap.to(outgoing, {
                yPercent: sign * -TRAVEL,
                duration: DURATION,
                ease: ODOMETER_EASE,
                overwrite: true,
                onComplete: () => {
                    gsap.set(outgoing, { opacity: 0 })
                }
            })
        ]

        return () => {
            tweens.forEach((tween) => tween.kill())
        }
    }, [value, direction])

    return (
        <span
            aria-live='polite'
            className={cn('relative inline-block overflow-hidden tabular-nums', className)}
        >
            <span className='sr-only'>
                {value}
            </span>

            {/* Reserves the box so the clip never collapses mid-swap. */}
            <span aria-hidden='true' className='invisible block'>
                {value}
            </span>

            <span ref={outgoingRef} aria-hidden='true' className='absolute inset-0 block opacity-0' />

            <span ref={incomingRef} aria-hidden='true' className='absolute inset-0 block'>
                {value}
            </span>
        </span>
    )
}
